import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaRegClock, FaIdBadge, FaLayerGroup } from 'react-icons/fa';
import DailyBrief from '../PoliticalProfile/DailyBrief';
import { useAuth } from '../../contexts/AuthContext';
import {
  TEAMS, QUICK_ACTIONS, canSee, getRecentSections,
} from '../../config/teams';

/* ============================================================
   Centro de mando — la nueva home. Responde "¿qué hago ahora?":
   saludo + resumen del día + accesos rápidos + áreas + recientes.
   ============================================================ */

const mono = 'var(--font-mono)';

const Page = styled.div`
  position: relative;
  padding-bottom: 1rem;
`;

const Hero = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;

  h1 {
    margin: 0;
    font-size: 1.6rem;
    line-height: 1.15;
    color: var(--text-primary);
  }
  .sub {
    margin: 0.35rem 0 0;
    font-size: 0.9rem;
    color: var(--text-secondary);
  }
  .date {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    white-space: nowrap;
    padding-top: 0.4rem;
  }
`;

const ProfileLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.7rem;
  font-size: 0.8rem;
  color: var(--btn-primary);
  text-decoration: none;
  &:hover { text-decoration: underline; }
  svg { font-size: 0.78rem; }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.75rem 0 0.75rem;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-soft);
  }
`;

/* --- Accesos rápidos --- */
const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.7rem;
`;

const QuickCard = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem;
  background: var(--surface-deep);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  text-decoration: none;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $color }) => ($color ? `${$color}66` : 'rgba(0,180,216,0.4)')};
    background: rgba(255, 255, 255, 0.02);
  }
  &:hover .go { opacity: 1; transform: translateX(0); }

  .ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: var(--radius-sm);
    font-size: 0.95rem;
    color: ${({ $color }) => $color || 'var(--btn-primary)'};
    background: ${({ $color }) => ($color ? `${$color}1a` : 'rgba(0,180,216,0.1)')};
    border: 1px solid ${({ $color }) => ($color ? `${$color}44` : 'rgba(0,180,216,0.28)')};
  }
  .body { flex: 1; min-width: 0; }
  .label {
    display: block;
    font-size: 0.92rem;
    font-weight: 500;
    color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  /* La descripción es lo que explica para qué sirve la sección: se muestra
     completa en hasta dos líneas en vez de cortarse a mitad de palabra. */
  .desc {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 0.74rem;
    line-height: 1.35;
    color: var(--text-disabled);
    margin-top: 2px;
  }
  .go {
    color: ${({ $color }) => $color || 'var(--btn-primary)'};
    font-size: 0.75rem;
    opacity: 0.4;
    transition: opacity 0.15s ease, transform 0.15s ease;
    flex-shrink: 0;
    margin-top: 0.3rem;
  }
`;

/* --- Acceso al hub de áreas --- */
const AllAreasLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 1.75rem;
  padding: 0.7rem 1rem;
  background: var(--surface);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  svg { font-size: 0.85rem; color: var(--btn-primary); }
  .go { font-size: 0.75rem; opacity: 0.5; }

  &:hover {
    color: var(--text-primary);
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(255, 255, 255, 0.03);
  }
  &:hover .go { opacity: 1; }
`;

/* --- Recientes --- */
const RecentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const RecentChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.7rem;
  background: var(--surface-deep);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: border-color 0.15s ease, color 0.15s ease;

  svg { font-size: 0.75rem; color: ${({ $color }) => $color || 'var(--btn-primary)'}; }
  &:hover { color: var(--text-primary); border-color: rgba(0, 180, 216, 0.4); }
`;

function greeting(hour) {
  if (hour < 6) return 'Buenas noches';
  if (hour < 13) return 'Buenos días';
  if (hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

const CommandCenter = () => {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const now = new Date();

  const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0];
  const hello = greeting(now.getHours());
  const dateLabel = now.toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const quick = useMemo(
    () => QUICK_ACTIONS.filter((q) => canSee(q.to, role)),
    [role]
  );
  const areaCount = useMemo(
    () => TEAMS.filter((t) => t.tools.some((x) => canSee(x.to, role))).length,
    [role]
  );
  const recents = useMemo(() => getRecentSections(role, 6), [role]);

  return (
    <Page>
      <Hero>
        <div>
          <h1>{hello}{firstName ? `, ${firstName}` : ''}.</h1>
          <p className="sub">Esto es lo que está pasando en tu operación política hoy.</p>
          <ProfileLink to="/perfil">
            <FaIdBadge /> Ver el perfil del candidato
          </ProfileLink>
        </div>
        <span className="date">{dateLabel}</span>
      </Hero>

      {/* Resumen del día (se autoabastece) */}
      <DailyBrief />

      {/* Accesos rápidos */}
      {quick.length > 0 && (
        <>
          <SectionTitle>Accesos rápidos</SectionTitle>
          <QuickGrid>
            {quick.map((item) => {
              const Icon = item.icon;
              return (
                <QuickCard key={item.to} to={item.to} $color={item.teamColor}>
                  <span className="ico"><Icon /></span>
                  <span className="body">
                    <span className="label">{item.label}</span>
                    <span className="desc">{item.desc}</span>
                  </span>
                  <FaArrowRight className="go" />
                </QuickCard>
              );
            })}
          </QuickGrid>
        </>
      )}

      {/* Recientes */}
      {recents.length > 0 && (
        <>
          <SectionTitle><FaRegClock style={{ fontSize: '0.7rem' }} /> Seguí donde estabas</SectionTitle>
          <RecentRow>
            {recents.map((item) => {
              const Icon = item.icon;
              return (
                <RecentChip key={item.to} to={item.to} $color={item.teamColor}>
                  <Icon /> {item.label}
                </RecentChip>
              );
            })}
          </RecentRow>
        </>
      )}

      {/* El listado completo de áreas vive en el menú lateral y en /equipos:
          repetirlo acá sumaba una tercera lista de navegación a la home. */}
      <AllAreasLink to="/equipos">
        <FaLayerGroup />
        Ver las {areaCount} áreas y todas sus herramientas
        <FaArrowRight className="go" />
      </AllAreasLink>
    </Page>
  );
};

export default CommandCenter;
